/**
 * Falling blocks (sand, gravel, anvils, the dragon egg) in the air.
 *
 * Same trick as the primed TNT cubes: the block's own geometry (six cube faces
 * from its tiles, or its model quads for the anvil) in the mesher's packed
 * vertex format on the opaque voxel material, so it gets the atlas, lighting
 * and fog for free. The light is the cell the block is in, rewritten into the
 * z word of `auv` whenever it changes. Positions interpolate from the previous
 * tick by the frame's alpha.
 */

import * as THREE from 'three';
import { BLOCKS, RenderShape } from '../blocks';
import { modelQuads } from '../models';
import { packLightMeta, packTile, POS_SCALE, UV_SCALE } from '../mesher/greedy';
import type { World } from '../world/chunk';

export interface FallingView {
  /** Identity (a sim block, or a replicated entity id). */
  key: object | number;
  id: number;
  x: number; y: number; z: number;
  px: number; py: number; pz: number;
}

interface Template { pos: Float32Array; uv: Float32Array; tile: number[]; face: number[] }

interface Entry { mesh: THREE.Mesh; geometry: THREE.BufferGeometry; auv: Uint16Array; attr: THREE.BufferAttribute; faces: number[]; light: number }

const NO_NB = { px: 0, nx: 0, pz: 0, nz: 0 };

export class FallingBlockRenderer {
  readonly group = new THREE.Group();
  private readonly entries = new Map<object | number, Entry>();
  private readonly templates = new Map<number, Template>();

  constructor(private readonly material: THREE.Material, private readonly world: World) {
    this.group.frustumCulled = false;
  }

  get count(): number { return this.entries.size; }

  update(views: readonly FallingView[], alpha: number): void {
    const live = new Set<object | number>();
    for (const v of views) live.add(v.key);
    for (const [k, e] of this.entries) {
      if (live.has(k)) continue;
      this.group.remove(e.mesh);
      e.geometry.dispose();
      this.entries.delete(k);
    }
    for (const v of views) {
      const x = v.px + (v.x - v.px) * alpha, y = v.py + (v.y - v.py) * alpha, z = v.pz + (v.z - v.pz) * alpha;
      const light = this.lightAt(x, y, z);
      let e = this.entries.get(v.key);
      if (!e) {
        e = this.spawn(v.id, light);
        this.entries.set(v.key, e);
      } else if (e.light !== light) {
        e.light = light;
        for (let i = 0; i < e.faces.length; i++) e.auv[i * 4 + 2] = packLightMeta(light, e.faces[i]);
        e.attr.needsUpdate = true;
      }
      e.mesh.position.set(x, y, z);
    }
  }

  private lightAt(x: number, y: number, z: number): number {
    const bx = Math.floor(x), by = Math.floor(y + 0.5), bz = Math.floor(z);
    return this.world.getSky(bx, by, bz) | (this.world.getBlockLight(bx, by, bz) << 4);
  }

  private template(id: number): Template {
    let t = this.templates.get(id);
    if (t) return t;
    const quads: { pos: readonly number[]; uv: readonly number[]; tile: number; face: number }[] = [];
    const def = BLOCKS[id];
    if (def.shape === RenderShape.Model) {
      for (const q of modelQuads(id, NO_NB)) quads.push(q);
    } else {
      const P: number[][][] = [
        [[1, 0, 1], [1, 0, 0], [1, 1, 0], [1, 1, 1]], [[0, 0, 0], [0, 0, 1], [0, 1, 1], [0, 1, 0]],
        [[0, 1, 0], [0, 1, 1], [1, 1, 1], [1, 1, 0]], [[0, 0, 0], [1, 0, 0], [1, 0, 1], [0, 0, 1]],
        [[0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1]], [[1, 0, 0], [0, 0, 0], [0, 1, 0], [1, 1, 0]],
      ];
      const U: number[][][] = [
        [[1, 1], [0, 1], [0, 0], [1, 0]], [[0, 1], [1, 1], [1, 0], [0, 0]],
        [[0, 1], [0, 0], [1, 0], [1, 1]], [[0, 0], [1, 0], [1, 1], [0, 1]],
        [[0, 1], [1, 1], [1, 0], [0, 0]], [[1, 1], [0, 1], [0, 0], [1, 0]],
      ];
      for (let f = 0; f < 6; f++) quads.push({ pos: P[f].flat(), uv: U[f].flat(), tile: def.tiles[f], face: f });
    }
    t = { pos: new Float32Array(quads.length * 12), uv: new Float32Array(quads.length * 8), tile: [], face: [] };
    quads.forEach((q, i) => {
      t!.pos.set(q.pos, i * 12);
      t!.uv.set(q.uv, i * 8);
      t!.tile.push(q.tile); t!.face.push(q.face);
    });
    this.templates.set(id, t);
    return t;
  }

  private spawn(id: number, light: number): Entry {
    const t = this.template(id);
    const n = t.tile.length * 4;
    const apos = new Uint16Array(n * 4), auv = new Uint16Array(n * 4), acolor = new Uint8Array(n * 4);
    const faces: number[] = [];
    const index: number[] = [];
    for (let q = 0; q < t.tile.length; q++) {
      for (let k = 0; k < 4; k++) {
        const v = q * 4 + k, o = v * 4;
        // the block's cell, centred on x/z with its bottom at the entity's feet
        apos[o] = Math.round((t.pos[q * 12 + k * 3] - 0.5 + 1) * POS_SCALE);
        apos[o + 1] = Math.round((t.pos[q * 12 + k * 3 + 1] + 1) * POS_SCALE);
        apos[o + 2] = Math.round((t.pos[q * 12 + k * 3 + 2] - 0.5 + 1) * POS_SCALE);
        apos[o + 3] = packTile(t.tile[q]);
        auv[o] = Math.round(t.uv[q * 8 + k * 2] * UV_SCALE);
        auv[o + 1] = Math.round(t.uv[q * 8 + k * 2 + 1] * UV_SCALE);
        auv[o + 2] = packLightMeta(light, t.face[q]);
        acolor[o] = acolor[o + 1] = acolor[o + 2] = acolor[o + 3] = 255;
        faces.push(t.face[q]);
      }
      const b = q * 4;
      index.push(b, b + 1, b + 2, b, b + 2, b + 3);
    }
    const geo = new THREE.BufferGeometry();
    const attr = new THREE.BufferAttribute(auv, 4);
    geo.setAttribute('apos', new THREE.BufferAttribute(apos, 4));
    geo.setAttribute('auv', attr);
    geo.setAttribute('acolor', new THREE.BufferAttribute(acolor, 4, true));
    geo.setIndex(new THREE.BufferAttribute(new Uint16Array(index), 1));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 64);
    const mesh = new THREE.Mesh(geo, this.material);
    mesh.frustumCulled = false;
    this.group.add(mesh);
    return { mesh, geometry: geo, auv, attr, faces, light };
  }
}
